<script lang="ts">
import { defineComponent, PropType } from 'vue'
import tippy from 'tippy.js'

type Trigger = 'mouseenter' | 'click'
type TagName = 'span' | 'div'
type TipPosition =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'right'
  | 'right-start'
  | 'right-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'auto'
  | 'auto-start'
  | 'auto-end'

export default defineComponent({
  name: 'NrTooltip',
  props: {
    tagName: {
      type: String as PropType<TagName>,
      default: 'span'
    },
    trigger: {
      type: String as PropType<Trigger>,
      default: 'mouseenter'
    },
    position: {
      type: String as PropType<TipPosition>,
      default: 'bottom'
    },
    // This label prop will be rendered as plain text. If you need to render HTML, please use the label slot instead.
    label: {
      type: String,
      default: 'Tooltip content goes here'
    },
    parentTarget: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return { tip: undefined }
  },

  mounted() {
    const targetEl = this.parentTarget
      ? this.$el.parentElement
      : this.$refs.trigger

    if (!targetEl) {
      return
    }

    this.tip = tippy(targetEl, {
      // allowHTML should not ever be needed here because we are passing in a DOM element as the content and not a string.
      allowHTML: false,
      arrow: true,
      interactive: true,
      content: this.$refs.label,
      placement: this.position,
      trigger: this.trigger,
      appendTo: document.body
    })
    window.addEventListener('keydown', this.handleEscKey)
  },

  methods: {
    handleEscKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        this.tip?.hide()
      }
    }
  },

  beforeUnmount() {
    this.tip?.destroy()
    this.tip = undefined
    window.removeEventListener('keydown', this.handleEscKey)
  }
})
</script>

<template>
  <component :is="tagName" class="nr-tooltip">
    <template v-if="$slots.label">
      <div ref="label" class="nr-tooltip__content">
        <slot name="label"></slot>
      </div>
      <div
        ref="trigger"
        class="nr-tooltip__trigger"
        role="link"
        aria-label="Tooltip Trigger"
      >
        <slot />
      </div>
    </template>
    <template v-else>
      <template v-if="label">
        <div ref="label" class="nr-tooltip__content">{{ label }}</div>
        <div
          ref="trigger"
          class="nr-tooltip__trigger"
          role="link"
          aria-label="Tooltip Trigger"
        >
          <slot />
        </div>
      </template>
      <template v-else>
        <slot />
      </template>
    </template>
  </component>
</template>

<style lang="scss">
@import 'tippy.js/dist/tippy.css';

.nr-tooltip__trigger {
  display: inline-block;
}
</style>
